<template>
  <Teleport to="body">
    <section
      v-if="showRoulette"
      class="roulette-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="roulette-title"
      @click.self="closeRoulette"
    >
      <div class="roulette-card">
        <button type="button" class="roulette-close" aria-label="Cerrar ruleta" @click="closeRoulette">
          &times;
        </button>

        <div class="roulette-header">
          <span class="roulette-badge">OFERTA ESPECIAL</span>
          <h2 id="roulette-title">Juga y gana</h2>
          <p>Gira la ruleta y recibi un cupon unico para tu email.</p>
        </div>

        <div class="roulette-layout">
          <div class="wheel-wrapper">
            <div class="pointer"></div>

            <div
              class="wheel"
              :style="{ transform: `rotate(${rotation}deg)`, background: wheelBackground }"
            >
              <div class="wheel-lines" :style="{ background: wheelLinesBackground }"></div>
              <div
                v-for="(item, index) in prizes"
                :key="item.id || item.label"
                class="wheel-label"
                :style="getTextStyle(index)"
              >
                {{ item.label }}
              </div>
            </div>

            <button
              type="button"
              class="wheel-center"
              :disabled="isSpinning || !!result || !prizes.length"
              aria-label="Girar ruleta"
              @click="spinWheel"
            >
              <span>{{ isSpinning ? "..." : "GO" }}</span>
            </button>
          </div>

          <div class="form-panel">
            <div v-if="!result" class="form-content">
              <label for="roulette-email">Tu correo electronico</label>
              <input
                id="roulette-email"
                v-model="email"
                type="email"
                placeholder="tu@email.com"
                autocomplete="email"
                :disabled="isSpinning"
                @keyup.enter="spinWheel"
              />
              <p v-if="errorMessage" class="roulette-error fw-normal">{{ errorMessage }}</p>

              <button type="button" :disabled="isSpinning || !prizes.length" @click="spinWheel">
                {{ isSpinning ? "Girando..." : "Girar ahora" }}
              </button>

              <small>El cupon queda asociado a este email y vence con contador.</small>
            </div>

            <div v-else class="result-box">
              <span class="result-label">{{ alreadySpun ? "Cupon activo" : "Ganaste" }}</span>
              <h3>{{ result.label }}</h3>
              <p>Usa este cupon en tu compra:</p>

              <div class="coupon">{{ result.code }}</div>

              <div class="coupon-countdown">
                Vence en <strong>{{ timeRemainingText }}</strong>
              </div>

              <button type="button" @click="copyCoupon">
                {{ copied ? "Copiado" : "Copiar cupon" }}
              </button>

              <button type="button" class="secondary-button" @click="closeRoulette">
                Cerrar
              </button>

              <small>Solo se puede usar con {{ result.assigned_email || email }}.</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<script>
import { getRouletteConfig, spinRoulette } from "@/services/coupons";

const fallbackPrizes = [
  { label: "$3.000 OFF", type: "fixed", value: 3000, color: "#ffffff", text_color: "#111111" },
  { label: "5% OFF", type: "percentage", value: 5, color: "#d60000", text_color: "#ffffff" },
  { label: "$5.000 OFF", type: "fixed", value: 5000, color: "#ffffff", text_color: "#111111" },
  { label: "10% OFF", type: "percentage", value: 10, color: "#d60000", text_color: "#ffffff" },
];

export default {
  name: "PromoRoulette",

  data() {
    return {
      email: "",
      rotation: 0,
      isSpinning: false,
      result: null,
      copied: false,
      showRoulette: false,
      prizes: [],
      errorMessage: "",
      expiresAt: null,
      nowTick: Date.now(),
      countdownTimer: null,
      alreadySpun: false,
    };
  },

  computed: {
    segmentAngle() {
      return this.prizes.length ? 360 / this.prizes.length : 360;
    },

    normalizedRotation() {
      return ((this.rotation % 360) + 360) % 360;
    },

    wheelBackground() {
      if (!this.prizes.length) return "#d60000";
      const stops = this.prizes.map((prize, index) => {
        const start = index * this.segmentAngle;
        const end = (index + 1) * this.segmentAngle;
        return `${prize.color || "#ffffff"} ${start}deg ${end}deg`;
      });
      return `conic-gradient(from -90deg, ${stops.join(", ")})`;
    },

    wheelLinesBackground() {
      if (!this.prizes.length) return "transparent";
      const lines = this.prizes.map((_, index) => {
        const angle = index * this.segmentAngle;
        return `linear-gradient(${angle}deg, transparent calc(50% - 1px), rgba(0,0,0,.32) 50%, transparent calc(50% + 1px))`;
      });
      return lines.join(", ");
    },

    timeRemainingText() {
      if (!this.expiresAt) return "--:--";
      const diff = Math.max(0, new Date(this.expiresAt).getTime() - this.nowTick);
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
  },

  async mounted() {
    if (typeof window === "undefined") return;
    await this.loadPrizes();
    if (!this.prizes.length) return;
    this.email = window.localStorage.getItem("rouletteEmail") || "";
    this.showRoulette = !window.localStorage.getItem("rouletteDismissed");
    if (this.showRoulette) {
      document.body.classList.add("roulette-open");
      window.addEventListener("keydown", this.handleKeydown);
    }
  },

  beforeUnmount() {
    if (typeof window === "undefined") return;
    this.stopCountdown();
    document.body.classList.remove("roulette-open");
    window.removeEventListener("keydown", this.handleKeydown);
  },

  methods: {
    async loadPrizes() {
      try {
        const config = await getRouletteConfig();
        const prizes = Array.isArray(config?.prizes) ? config.prizes : [];
        this.prizes = prizes.length ? prizes : fallbackPrizes;
      } catch (error) {
        console.error("No se pudo cargar la ruleta", error);
        this.prizes = fallbackPrizes;
      }
    },

    getTextStyle(index) {
      const angle = index * this.segmentAngle + this.segmentAngle / 2;
      const prize = this.prizes[index] || {};
      const textColor = prize.text_color || this.getReadableTextColor(prize.color);
      return {
        transform: `rotate(${angle}deg) translateY(calc(var(--wheel-size) * -0.32)) rotate(${-angle}deg)`,
        color: textColor,
      };
    },

    getReadableTextColor(color) {
      const rgb = this.parseColorToRgb(color);
      if (!rgb) return "#ffffff";
      const [r, g, b] = rgb;
      const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      return luminance > 0.6 ? "#111111" : "#ffffff";
    },

    parseColorToRgb(color) {
      if (!color) return null;
      const value = String(color).trim().toLowerCase();

      if (value.startsWith("#")) {
        const hex = value.replace("#", "");
        if (hex.length === 3) {
          const r = parseInt(hex[0] + hex[0], 16);
          const g = parseInt(hex[1] + hex[1], 16);
          const b = parseInt(hex[2] + hex[2], 16);
          return [r, g, b];
        }
        if (hex.length === 6) {
          const r = parseInt(hex.slice(0, 2), 16);
          const g = parseInt(hex.slice(2, 4), 16);
          const b = parseInt(hex.slice(4, 6), 16);
          return [r, g, b];
        }
      }

      const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (match) {
        return [Number(match[1]), Number(match[2]), Number(match[3])];
      }

      if (value === "white") return [255, 255, 255];
      if (value === "black") return [0, 0, 0];

      return null;
    },

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((email || "").trim());
    },

    async spinWheel() {
      if (this.isSpinning || this.result) return;
      this.errorMessage = "";
      const cleanEmail = this.email.trim().toLowerCase();

      if (!this.isValidEmail(cleanEmail)) {
        this.errorMessage = "Ingresa un email valido, por ejemplo nombre@gmail.com.";
        return;
      }

      this.email = cleanEmail;
      this.isSpinning = true;
      this.copied = false;

      try {
        const response = await spinRoulette(cleanEmail);
        const prize = response.prize || {};
        const coupon = response.coupon || {};
        const prizeIndex = Math.max(0, this.prizes.findIndex((item) => item.id === prize.id));
        const centerAngle = prizeIndex * this.segmentAngle + this.segmentAngle / 2;
        const desiredRotation = (360 - centerAngle) % 360;
        const correction = (desiredRotation - this.normalizedRotation + 360) % 360;

        this.rotation += 6 * 360 + correction;
        this.alreadySpun = !!response.already_spun;
        this.expiresAt = response.expires_at || coupon.expires_at;
        window.localStorage.setItem("rouletteEmail", cleanEmail);

        window.setTimeout(() => {
          this.result = {
            ...prize,
            ...coupon,
            label: prize.label || coupon.code,
            code: coupon.code,
          };
          this.isSpinning = false;
          this.startCountdown();
        }, 4200);
      } catch (error) {
        const data = error.response?.data || {};
        this.errorMessage = data.error || data.detail || "No se pudo girar la ruleta. Intentalo de nuevo.";
        this.isSpinning = false;
      }
    },

    startCountdown() {
      this.stopCountdown();
      this.nowTick = Date.now();
      this.countdownTimer = window.setInterval(() => {
        this.nowTick = Date.now();
        if (this.expiresAt && new Date(this.expiresAt).getTime() <= this.nowTick) {
          this.stopCountdown();
        }
      }, 1000);
    },

    stopCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },

    async copyCoupon() {
      if (!this.result?.code) return;
      try {
        await navigator.clipboard.writeText(this.result.code);
        this.copied = true;
        window.setTimeout(() => {
          this.copied = false;
        }, 1800);
      } catch (error) {
        console.error("No se pudo copiar el cupon", error);
      }
    },

    handleKeydown(event) {
      if (event.key === "Escape" && !this.isSpinning) {
        this.closeRoulette();
      }
    },

    closeRoulette() {
      if (this.isSpinning) return;
      this.showRoulette = false;
      window.localStorage.setItem("rouletteDismissed", "1");
      document.body.classList.remove("roulette-open");
      window.removeEventListener("keydown", this.handleKeydown);
    },
  },
};
</script>

<style>
body.roulette-open {
  overflow: hidden;
}
</style>

<style scoped>
section.roulette-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
  min-height: 100dvh;
  padding: 24px 16px;
  display: flex;
  /* Centrado vía margin:auto en .roulette-card: centra cuando entra y
     permite scrollear desde el top cuando el card es más alto que el viewport
     (align-items:center cortaría el borde superior en pantallas chicas). */
  background: rgba(0, 0, 0, 0.78);
  color: #ffffff;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  overflow-y: auto;
}

.roulette-card {
  position: relative;
  margin: auto;
  width: 100%;
  max-width: 980px;
  padding: 34px;
  border-radius: 18px;
  background: rgba(15, 15, 15, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 32px 90px rgba(0, 0, 0, 0.55);
}

.roulette-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 20;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.roulette-header {
  text-align: center;
  margin-bottom: 30px;
}

.roulette-badge {
  display: inline-flex;
  padding: 9px 18px;
  margin-bottom: 16px;
  border-radius: 999px;
  background: rgba(214, 0, 0, 0.15);
  border: 1px solid rgba(214, 0, 0, 0.55);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.roulette-header h2 {
  margin: 0;
  color: #ffffff;
  font-size: clamp(34px, 5vw, 58px);
  line-height: 1.02;
  font-weight: 900;
}

.roulette-header p {
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 16px;
}

.roulette-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 36px;
  align-items: center;
}

.wheel-wrapper {
  --wheel-size: min(390px, 82vw, 52vh);
  position: relative;
  width: var(--wheel-size);
  height: var(--wheel-size);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pointer {
  position: absolute;
  top: -7px;
  left: 50%;
  z-index: 12;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 19px solid transparent;
  border-right: 19px solid transparent;
  border-top: 38px solid #ffffff;
  filter: drop-shadow(0 5px 8px rgba(0, 0, 0, 0.45));
}

.wheel {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  border: 10px solid #1b1b1b;
  box-shadow: 0 0 0 13px rgba(214, 0, 0, 0.25), 0 24px 55px rgba(0, 0, 0, 0.65);
  transition: transform 4.2s cubic-bezier(0.12, 0.78, 0.12, 1);
}

.wheel::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 4;
  border-radius: 50%;
  background: radial-gradient(circle, transparent 42%, rgba(0, 0, 0, 0.22) 43%, rgba(0, 0, 0, 0.38) 100%);
  pointer-events: none;
}

.wheel-lines {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 50%;
  pointer-events: none;
}

.wheel-label {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 6;
  width: 132px;
  margin-left: -66px;
  margin-top: -14px;
  text-align: center;
  font-size: 15px;
  line-height: 1.05;
  font-weight: 950;
  text-transform: uppercase;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.28);
  pointer-events: none;
}

.wheel-center {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 15;
  width: 88px;
  height: 88px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 35% 30%, #ffffff, #ff3b3b 32%, #b80000 78%);
  border: 6px solid #ffffff;
  box-shadow: 0 0 0 8px rgba(214, 0, 0, 0.28), 0 12px 22px rgba(0, 0, 0, 0.45);
  cursor: pointer;
}

.wheel-center:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.wheel-center span {
  color: #ffffff;
  font-size: 18px;
  font-weight: 950;
}

.form-panel {
  padding: 28px;
  border-radius: 16px;
  background: #ffffff;
  color: #111111;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
}

.form-content,
.result-box {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-panel label {
  color: #171717;
  font-size: 14px;
  font-weight: 800;
}

.form-panel input {
  width: 100%;
  height: 54px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid #d8d8d8;
  outline: none;
  background: #f7f7f7;
  color: #111111;
  font-size: 15px;
}

.form-panel input:focus {
  border-color: #d60000;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(214, 0, 0, 0.13);
}

.roulette-error {
  margin: -4px 0 0;
  color: #b80000;
  font-size: 13px;
  font-weight: 700;
}

.form-panel button {
  width: 100%;
  min-height: 54px;
  margin-top: 4px;
  border: none;
  border-radius: 12px;
  background: #d60000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 900;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(214, 0, 0, 0.28);
}

.form-panel button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: none;
}

.form-panel .secondary-button {
  margin-top: 0;
  background: #111111;
  box-shadow: none;
}

.form-panel small {
  color: #5f5f5f;
  text-align: center;
  font-size: 12px;
}

.result-label {
  width: fit-content;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(214, 0, 0, 0.1);
  color: #d60000;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.result-box h3 {
  margin: 0;
  color: #111111;
  font-size: 34px;
  line-height: 1;
  font-weight: 950;
}

.result-box p {
  margin: 0;
  color: #555555;
}

.coupon,
.coupon-countdown {
  padding: 18px;
  border-radius: 12px;
  background: #f5f5f5;
  border: 1px dashed #d60000;
  color: #d60000;
  text-align: center;
  font-size: 24px;
  font-weight: 950;
  letter-spacing: 0.08em;
}

.coupon-countdown {
  padding: 12px;
  color: #111111;
  border-color: #dddddd;
  font-size: 15px;
  letter-spacing: 0;
}

@media (max-width: 850px) {
  .roulette-card {
    padding: 24px 18px;
  }

  .roulette-header {
    margin-bottom: 22px;
  }

  .roulette-layout {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .form-panel {
    padding: 22px;
  }

  .wheel-wrapper {
    --wheel-size: min(340px, 86vw, 48vh);
  }

  .wheel-label {
    font-size: 13px;
  }
}

@media (max-width: 420px) {
  .roulette-modal {
    padding: calc(var(--navbar-total-height-mobile, 72px) + 12px) 14px 20px;
    align-items: flex-start;
  }

  .roulette-card {
    padding: 28px 14px 18px;
  }

  .roulette-close {
    top: 10px;
    right: 10px;
    width: 34px;
    height: 34px;
    font-size: 24px;
  }

  .roulette-header {
    margin: 4px 24px 16px;
  }

  .roulette-badge {
    padding: 7px 12px;
    margin-bottom: 10px;
    font-size: 10px;
  }

  .roulette-header h2 {
    color: #ffffff;
    font-size: 32px;
    line-height: 1.06;
  }

  .roulette-header p {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.35;
  }

  .roulette-layout {
    gap: 20px;
  }

  .wheel-wrapper {
    --wheel-size: min(270px, 78vw, 44vh);
  }

  .wheel-label {
    width: 104px;
    margin-left: -52px;
    font-size: 11px;
  }

  .wheel-center {
    width: 68px;
    height: 68px;
  }

  .form-panel {
    padding: 18px;
  }
}

@media (max-width: 991.98px) {
  .roulette-modal {
    padding-top: calc(var(--navbar-total-height-mobile, 72px) + 16px);
    align-items: flex-start;
  }
}
</style>
